import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { ImageOff, QrCode } from "lucide-react";
import { useEffect, useState } from "react";

import { thumbnailSrc } from "../api";
import type { PanelDrawing, QrStampArea } from "./types";

/**
 * QR を押す前に、**ページのどの区画から空白を探すか**を選ぶダイアログ。
 *
 * 押すのは QR と、押印欄（加工者 / 検査者 / 検査責任者）の 2 つ。**別々の空白へ
 * 置く**ので並びはページごとに変わる。押す位置そのものは今までどおり「空白が
 * いちばん広いところ」を測って決める。紙の右下へ入る 1 行（社名・出荷予定日・
 * 直送先コード・工番）は**位置が決まっている**ので、ここでは選ばせない。
 * ただし空白の広さだけでは、客先の押印欄のように**空けておきたい場所**までは
 * 分からない。そこでページを上下左右に 4 分割し、どの区画から探すかだけを
 * 選んでもらう。
 *
 * **区画は絵の上で選ばせる**。「左上」「右下」と字で並べても、紙を思い浮かべ
 * ながら読み替えることになり、選び間違いに押してから気づく。図面のサムネイルに
 * 田の字を重ねて、押したい側をそのまま押してもらう形にしてある。
 *
 * **刷る大きさもここで選ぶ**（100%〜50%）。紙や図面によっては既定の大きさが
 * 大きすぎる。押印欄だけは倍率の外で、いつでも実寸で刷る（朱肉を押す枠なので
 * 小さくすると用を成さない）。
 *
 * サムネイルがまだ無い図面（取り込み直後・PDF 無し）では白紙の枠に田の字だけを
 * 出す。絵が無いこと自体は選択の邪魔にならない。
 *
 * まとめて押すときは**選んだ全部に同じ区画**を使う。1 枚ずつ選ばせると、数十枚を
 * 押す操作が数十回の選択になり、まとめて押す意味が無くなる。代表として 1 枚の
 * 絵を出し、何枚に押すのかを文言で添える。
 */
export default function QrAreaDialog({
  isOpen,
  drawing,
  targetCount,
  isBusy,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  /** 絵に出す図面（まとめて押すときは代表の 1 枚）。null なら白紙の枠を出す。 */
  drawing: PanelDrawing | null;
  /** 押す枚数。1 なら枚数は出さない。 */
  targetCount: number;
  isBusy: boolean;
  onClose: () => void;
  onConfirm: (area: QrStampArea, scalePercent: number) => void;
}) {
  // 既定は「おまかせ」。これまでと同じ動きなので、選ばずに押しても結果が変わらない。
  const [area, setArea] = useState<QrStampArea>("auto");
  const [scalePercent, setScalePercent] = useState(100);
  const [imageFailed, setImageFailed] = useState(false);

  // 開くたびに選び直す（前回の選択を持ち越すと、別の図面に前の区画で押される）。
  useEffect(() => {
    if (!isOpen) return;
    setArea("auto");
    setScalePercent(100);
    setImageFailed(false);
  }, [isOpen]);

  const thumbnail = thumbnailSrc(drawing?.thumbnail_path ?? null);
  const showImage = thumbnail != null && !imageFailed;

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()} size="2xl">
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>QR を押す場所</span>
          <span className="text-small font-normal text-default-500">
            {targetCount > 1
              ? `選んだ ${targetCount} 枚すべてに、同じ区画で押します。`
              : "押したい側を選んでください。"}
            {"区画の中では、空白のいちばん広いところを自動で探します。"}
            {
              "押印欄（加工者・検査者・検査責任者）も、同じ区画の空いているところへ押します。"
            }
            {
              "紙の右下には 社名・出荷予定日・直送先コード・工番 を刷ります（空の項目は詰めます）。"
            }
          </span>
        </ModalHeader>

        <ModalBody>
          {/* 図面は A3 横が多い。カードと同じ縦横比にして、紙の形のまま選ばせる。 */}
          <div className="relative mx-auto aspect-[1.414/1] w-full max-w-xl overflow-hidden rounded-lg border border-default-200 bg-white">
            {showImage ? (
              <img
                src={thumbnail}
                alt=""
                className="absolute inset-0 h-full w-full object-contain"
                onError={() => setImageFailed(true)}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center gap-1.5 text-default-300">
                <ImageOff size={18} />
                <span className="text-tiny">絵がありません</span>
              </div>
            )}

            {/* 田の字。絵の上に重ね、4 つの区画をそのまま押せるようにする。 */}
            <div className="absolute inset-0 grid grid-cols-2 grid-rows-2">
              {AREAS.map((cell) => (
                <button
                  key={cell.key}
                  type="button"
                  aria-label={cell.label}
                  aria-pressed={area === cell.key}
                  data-testid={`qr-area-${cell.key}`}
                  onClick={() => setArea(cell.key)}
                  className={[
                    "flex items-center justify-center border border-dashed transition-colors",
                    area === cell.key
                      ? "border-brand-500 bg-brand-500/20"
                      : "border-default-300 hover:bg-brand-500/10",
                  ].join(" ")}
                >
                  {area === cell.key && (
                    <span className="flex items-center gap-1 rounded-full bg-brand-500 px-2 py-0.5 text-tiny font-bold text-white">
                      <QrCode size={12} />
                      ここに押す
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-tiny text-default-500">
              {area === "auto"
                ? "いまは紙全体から空白を探します（これまでと同じ動きです）。"
                : `選んだ区画（${AREAS.find((a) => a.key === area)?.label}）の中から空白を探します。` +
                  "区画が線で埋まっているときは、その中で墨のいちばん薄いところに重ねます。"}
            </p>
            <Button
              size="sm"
              radius="full"
              variant={area === "auto" ? "solid" : "bordered"}
              color="default"
              data-testid="qr-area-auto"
              onPress={() => setArea("auto")}
            >
              紙全体から探す
            </Button>
          </div>

          {/* 刷る大きさ。押印欄だけは倍率の外なので、そのことも添える。 */}
          <div className="flex flex-wrap items-center gap-2 border-t border-default-200 pt-3">
            <span className="text-small font-bold text-default-700">刷る大きさ</span>
            <div className="flex gap-1">
              {SCALES.map((value) => (
                <Button
                  key={value}
                  size="sm"
                  radius="full"
                  variant={scalePercent === value ? "solid" : "bordered"}
                  color={scalePercent === value ? "primary" : "default"}
                  data-testid={`qr-scale-${value}`}
                  onPress={() => setScalePercent(value)}
                >
                  {value}%
                </Button>
              ))}
            </div>
            <p className="w-full text-tiny text-default-500">
              {
                "QR・添え書き・覚書きの表・右下の 1 行が縮みます。押印欄は実寸のままです。"
              }
              {scalePercent <= 60 &&
                "（小さくするほど QR は読み取りにくくなります。現場で試してから使ってください）"}
            </p>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            キャンセル
          </Button>
          <Button
            color="primary"
            radius="full"
            data-testid="qr-area-confirm"
            startContent={<QrCode size={16} />}
            isLoading={isBusy}
            onPress={() => onConfirm(area, scalePercent)}
          >
            この場所に押す
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/**
 * 選べる刷る大きさ（％）。50 が下限で、これ以上小さくすると QR の升が 0.25mm を
 * 下回り、現場で読めない（API 側も 50 未満は断る）。
 */
const SCALES = [100, 80, 65, 50];

/** 田の字の並び順（左上 → 右上 → 左下 → 右下）。grid の流れと同じ。 */
const AREAS: { key: QrStampArea; label: string }[] = [
  { key: "top_left", label: "左上" },
  { key: "top_right", label: "右上" },
  { key: "bottom_left", label: "左下" },
  { key: "bottom_right", label: "右下" },
];
